import { AGWASuiteApiClientBase } from './agwasuite-api';
import { readTabSessionId } from '../utility/session-storage-keys';

const ACCOUNT_STORAGE_KEY = 'agwa-active-account';
const USERID_STORAGE_KEY = 'agwa-active-userid';

export class ApiFactory {
  private static token?: string;

  /**
   * Sets the auth token and user ID for authenticated API requests.
   * Both session ID and user ID are stored in sessionStorage for per-tab isolation.
   * @param token Optional auth token (unused with httpOnly cookies)
   * @param userId User ID for CSRF protection header
   */
  public static setAuthToken(token: string | undefined, userId: string) {
    this.token = token;
    // Store userId in sessionStorage for per-tab isolation
    if (userId) {
      sessionStorage.setItem(USERID_STORAGE_KEY, userId);
    } else {
      sessionStorage.removeItem(USERID_STORAGE_KEY);
    }
  }

  /**
   * Gets the account key this tab is acting as (per-tab, inherited by new tabs).
   *
   * NOT a session: it is derived from the user id and shared across tabs by design, so it answers
   * WHICH ACCOUNT this tab is acting as, never which tab. See the note in session-manager.ts.
   */
  public static getAccountKey(): string | undefined {
    return sessionStorage.getItem(ACCOUNT_STORAGE_KEY) ?? undefined;
  }

  /**
   * Gets this tab's session id, which is what the `X-AGWA-Tab-Session` header carries.
   *
   * NOT `X-Session-Id`. That header is the CREDENTIAL SELECTOR and carries the account key; sending
   * a per-tab GUID in it makes the server look for a cookie named after a GUID and fail every
   * request in the product, which is exactly what happened for one commit in #362.
   *
   * Read synchronously from storage, never from `SSEClient`: the stream is a consumer of this value
   * and no longer its owner, and `sse-client.ts` imports this module so reaching back would close a
   * cycle. The server issues the value; `services/tab-session.ts` obtains and stores it.
   *
   * Undefined until this tab has been issued one. That is a real state, not an error - a tab that
   * has just opened and not yet asked simply holds no locks.
   */
  public static getSessionId(): string | undefined {
    return readTabSessionId();
  }

  /**
   * Gets the current user ID from sessionStorage (per-tab).
   */
  public static getUserId(): string | undefined {
    return sessionStorage.getItem(USERID_STORAGE_KEY) ?? undefined;
  }

  public static authenticated<T extends AGWASuiteApiClientBase>(
    type: new (baseUrl?: string) => T,
    options?: {
      baseUrl?: string;
      token?: string;
      userId?: string;
      sessionId?: string;
      /**
       * The account key to select the auth cookie by, when it must NOT be the one this tab has
       * stored. Only a tab whose stored account no longer matches the person it acts as needs it
       * (D-143): it has to authenticate as that person to end their tab session.
       */
      accountKey?: string;
    }
  ): T {
    // Two different questions: userId names the ACCOUNT (CSRF header), sessionId names the TAB
    // (lock ownership). Conflating them is exactly what #362 corrects.
    const userId = options?.userId ?? ApiFactory.getUserId();
    // Account key selects the auth cookie; tab session identifies the lock holder. Two values.
    const accountKey = options?.accountKey ?? ApiFactory.getAccountKey();
    const tabSessionId = options?.sessionId ?? ApiFactory.getSessionId();

    return new type(options?.baseUrl).setAuthToken(
      options?.token ?? ApiFactory.token,
      userId ?? '',
      accountKey,
      tabSessionId
    );
  }

  public static anonymous<T extends AGWASuiteApiClientBase>(
    type: new (baseUrl?: string) => T,
    options?: {
      baseUrl?: string;
    }
  ): T {
    // Anonymous requests still carry the per-tab session: an anonymous caller holds no lock, but
    // the header must mean the same thing on every request or the server cannot trust it at all.
    return new type(options?.baseUrl).setSessionHeaders(
      ApiFactory.getAccountKey(),
      ApiFactory.getSessionId()
    );
  }
}
